import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import http from '../config/http-service';
import type { SendInquiryOrderPayload, SendInquiryOrderResult } from './types';
import { restaurantInquiriesEndpoint } from './use-create-restaurant-inquiry-api';

// POST api/v1/cms/restaurant-inquiries/:id/send-order — creates ONE order for ONE
// supplier from awarded (ACCEPTED) quotes, each line named by its quote's master
// product, and stamps quotes with orderId — terminal, 409 on any quote that was
// already ordered. It links no restaurant-supplier: the restaurant's receive does.
type TData = SendInquiryOrderResult;
type TError = AxiosError;
type TVariables = { id: number | string } & SendInquiryOrderPayload;

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

export function useSendInquiryOrderApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ id, ...payload }: TVariables): Promise<TData> => {
    const response = await http.post(`${restaurantInquiriesEndpoint}/${id}/send-order`, payload);
    return response.data?.data ?? response.data;
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [restaurantInquiriesEndpoint, 'send-order'],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([restaurantInquiriesEndpoint]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
